import type { ReactNode } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { InfoTip } from './InfoTip';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'elevated';
  onClick?: () => void;
}

export function Card({ children, className, padding = 'md', variant = 'default', onClick }: CardProps) {
  const paddingStyles = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  const variantStyles = {
    default: 'bg-surface-primary border border-border-light',
    outline: 'bg-transparent border border-border',
    elevated: 'bg-surface-primary shadow-soft',
  };

  return (
    <div
      className={clsx(
        'rounded-2xl',
        variantStyles[variant],
        paddingStyles[padding],
        onClick && 'cursor-pointer hover:border-border transition-colors',
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  title: string;
  /** ReactNode so a description can carry inline emphasis or links. */
  description?: ReactNode;
  action?: React.ReactNode;
}

/**
 * A panel title. Deliberately quiet — small and secondary-coloured — so the data
 * inside the card is what carries visual weight, matching the product dashboard.
 */
export function CardHeader({ title, description, action }: CardHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div className="min-w-0">
        <h3 className="text-body-md font-semibold text-text-primary">{title}</h3>
        {description && (
          <p className="text-body-xs text-text-tertiary mt-0.5">{description}</p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: string | number;
  /** A small muted suffix after the value ("/wk"), so the number stays the headline. */
  unit?: string;
  /**
   * Comparisons, one row each: the coloured pill with the change, then what it is
   * measured against in grey ("vs prior 90d"). Rows line up across a tile row.
   */
  compare?: Array<{ value: string; trend: 'up' | 'down' | 'neutral'; against: string }>;
  /** Muted line below the value — a baseline, a denominator, a period note. */
  sub?: ReactNode;
  change?: {
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  icon?: ReactNode;
  /** Hover tooltip on the label, for clarifying how the stat is derived. */
  tooltip?: string;
  /**
   * An (i) beside the label that opens on click: source, period and caveats, so
   * the tile itself keeps to the number and one line. Ignored on clickable tiles.
   */
  info?: ReactNode;
  /** When set, the tile becomes a button with a hover affordance. */
  onClick?: () => void;
  /** Suppresses the click affordance when there is nothing to drill into. */
  disabled?: boolean;
  /** `lg` is the hero KPI treatment; `md` suits dense multi-tile rows. */
  size?: 'md' | 'lg';
  /** @deprecated Every tile label is now the Space Mono eyebrow. */
  uppercaseLabel?: boolean;
}

/**
 * The single KPI tile. Replaces three parallel implementations that had drifted
 * apart on label case, value size, radius and padding.
 */
export function StatTile({
  label,
  value,
  unit,
  compare,
  sub,
  change,
  icon,
  tooltip,
  info,
  onClick,
  disabled,
  size = 'md',
}: StatTileProps) {
  const clickable = !!onClick && !disabled;

  // The tile is a size container so the value scales with the tile's width
  // instead of being cut off with "…" when a grid gets narrow.
  const body = (
    <div className="flex items-start justify-between gap-2 [container-type:inline-size]">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <p
            className={clsx('eyebrow truncate', tooltip && 'cursor-help')}
            title={tooltip}
          >
            {label}
          </p>
          {info && !clickable && <InfoTip label={`About ${label}`}>{info}</InfoTip>}
          {clickable && (
            <span
              aria-hidden
              className="text-text-tertiary text-body-xs leading-none opacity-0 group-hover:opacity-100 transition-opacity"
            >
              →
            </span>
          )}
        </div>
        <p
          className={clsx(
            'text-text-primary mt-3 tabular-nums font-light leading-none tracking-[-0.03em]',
            size === 'lg' ? 'text-[clamp(1.75rem,14cqi,3.5rem)]' : 'text-[clamp(1.5rem,12cqi,2.5rem)]'
          )}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
          {unit && <span className="ml-1 text-body-md tracking-normal text-text-tertiary">{unit}</span>}
        </p>
        {change && (
          <div className="mt-3">
            <TrendPill trend={change.trend}>{change.value}</TrendPill>
          </div>
        )}
        {compare && compare.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {compare.map((c) => (
              <div key={c.against} className="flex items-center gap-2 whitespace-nowrap">
                <TrendPill trend={c.trend}>{c.value}</TrendPill>
                <span className="text-body-xs text-text-tertiary truncate">{c.against}</span>
              </div>
            ))}
          </div>
        )}
        {sub && <p className={clsx('text-body-xs text-text-tertiary', compare?.length ? 'mt-2' : 'mt-1')}>{sub}</p>}
      </div>
      {icon && (
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-surface-secondary text-text-secondary">
          {icon}
        </div>
      )}
    </div>
  );

  if (!clickable) {
    return <Card padding="md">{body}</Card>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'group w-full h-full flex flex-col justify-start text-left rounded-2xl bg-surface-primary border border-border-light p-6',
        'transition-colors hover:border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30'
      )}
    >
      {body}
    </button>
  );
}

/**
 * A bare label/value stat with no card of its own, for stat rows nested inside a
 * larger panel. Keeps nested stats on the same scale as {@link StatTile} without
 * putting a card inside a card.
 */
export function InlineStat({
  label,
  value,
  sub,
  size = 'md',
}: {
  label: string;
  value: string | number;
  sub?: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div className="min-w-0">
      <p className="eyebrow truncate" title={label}>
        {label}
      </p>
      <p className={clsx('text-text-primary mt-2 tabular-nums', size === 'sm' ? 'text-heading-lg font-light' : 'text-stat-md')}>{value}</p>
      {sub && <p className="text-body-xs text-text-tertiary mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * A small bordered fact — a sync time, a row count, a date. Denser than
 * {@link InlineStat}, for the status strips above a panel's real content.
 */
export function MetaStat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-light bg-surface-secondary px-4 py-3">
      <p className="eyebrow">{label}</p>
      <p className="mt-1 text-body-sm font-medium text-text-primary">{value}</p>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  change?: {
    value: string;
    trend: 'up' | 'down' | 'neutral';
  };
  /** Optional muted secondary line under the change (e.g. "+48% vs pre-launch"). */
  secondary?: string;
  icon?: ReactNode;
  /** Optional hover tooltip on the label, for clarifying how the stat is derived. */
  tooltip?: string;
}

/** @deprecated Use {@link StatTile}. Kept so existing call sites keep working. */
export function StatCard({ label, value, change, secondary, icon, tooltip }: StatCardProps) {
  return (
    <StatTile label={label} value={value} change={change} sub={secondary} icon={icon} tooltip={tooltip} />
  );
}

/**
 * The one trend marker: a small tinted pill with an arrow — "↑ +19%", "↓ −59%",
 * "→ 0%". Green and red are status colours, so they only ever mean better/worse.
 */
export function TrendPill({
  trend,
  tone,
  children,
  className,
}: {
  trend: 'up' | 'down' | 'neutral';
  /** Colour override when direction and goodness differ — a cost going down is good. */
  tone?: 'good' | 'bad' | 'neutral';
  children: ReactNode;
  className?: string;
}) {
  const Icon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : ArrowRight;
  const t = tone ?? (trend === 'up' ? 'good' : trend === 'down' ? 'bad' : 'neutral');
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-body-xs font-medium tabular-nums whitespace-nowrap',
        t === 'good' && 'bg-success-light text-success-dark',
        t === 'bad' && 'bg-error-light text-error-dark',
        t === 'neutral' && 'bg-surface-tertiary text-text-tertiary',
        className
      )}
    >
      <Icon className="h-3 w-3 flex-none" aria-hidden />
      {children}
    </span>
  );
}
